import React, { useEffect, useMemo, useRef, useState } from "react";
import "./gallery.scss";
import {
    useGalleryMediaListQuery,
    useCreateGalleryMediaMutation,
    useDeleteGalleryMediaMutation,
    useUpdateGalleryMediaMutation,
} from "../../../Services/vehicle/Vehicle";
import { toast } from "react-toastify";
import EditGallery from "./EditGallery";
import DeleteConfirmationModal from "../banner/DeleteConfirmationModal";

interface GalleryItem {
    id: number;
    title: string;
    gallery: string;
    isVideo: boolean;
}

const Gallery: React.FC = () => {
    const [galleryFile, setGalleryFile] = useState<File | null>(null);
    const [galleryPreview, setGalleryPreview] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [createGalleryTrigger] = useCreateGalleryMediaMutation();
    const [deleteGalleryTrigger] = useDeleteGalleryMediaMutation();
    const [updateGalleryTrigger] = useUpdateGalleryMediaMutation();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const { data: galleryData, isLoading, refetch } = useGalleryMediaListQuery({ page, page_size: pageSize });

    const [totalItems, setTotalItems] = useState(0);
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const startIndex = Math.min((page - 1) * pageSize, Math.max(totalItems - 1, 0));
    const endIndex = Math.min(startIndex + pageSize, totalItems);

    const [items, setItems] = useState<GalleryItem[]>([]);

    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState<GalleryItem | null>(null);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [toDelete, setToDelete] = useState<GalleryItem | null>(null);

    useEffect(() => {
        if (galleryData?.results) {
            const normalized = (galleryData.results || []).map((it: any) => ({
                id: it.id,
                title: it.title,
                gallery: it.file || it.gallery || it.media_url || it.presigned_url,
                isVideo: it.content_type.startsWith("video"),
            }));
            setItems(normalized);
            setTotalItems(galleryData?.count ?? normalized.length);
        } else {
            setItems([]);
            setTotalItems(0);
        }
    }, [galleryData]);

    const nextId = useMemo(() => () => (items[items.length - 1]?.id ?? 0) + 1, [items]);

    const resetInputs = () => {
        setGalleryPreview(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Accept only images/videos
        const allowed = ["image/jpeg", "image/png", "image/jpg", "video/mp4"];
        if (!allowed.includes(file.type)) {
            toast.error("Only .jpg, .jpeg, .png, .mp4 are allowed");
            return;
        }
        setGalleryFile(file);
        setGalleryPreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!galleryFile) return;

        const formData = new FormData();
        // API expects raw file payload; using key 'file'
        formData.append("file", galleryFile);

        try {
            const response = await createGalleryTrigger(formData);
            if (response?.data) {
                toast.success("Gallery item uploaded");
                setGalleryFile(null);
                setGalleryPreview(null);
                if (fileRef.current) fileRef.current.value = "";
                refetch();
            } else {
                toast.error("Upload failed");
            }
        } catch {
            toast.error("Something went wrong");
        }
    };

    const openEdit = (item: GalleryItem) => {
        setEditing(item);
        setEditOpen(true);
    };

    const applyEdit = async (form: FormData) => {
        console.log(form,"test")
        if (!editing) return;
        try {
            const response = await updateGalleryTrigger({
                body: form,
                id: editing.id,
            });
            if ("data" in response) {
                toast.success("Gallery item updated");
                refetch();
            } else {
                toast.error("Update failed");
            }
        } catch {
            toast.error("Error occurred");
        } finally {
            setEditOpen(false);
            setEditing(null);
        }
    };

    const openDelete = (item: GalleryItem) => {
        setToDelete(item);
        setDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        try {
            const response = await deleteGalleryTrigger(toDelete.id);
            if ("data" in response || (response as any)?.data === undefined) {
                toast.success("Deleted successfully");
                refetch();
            } else {
                toast.error("Delete failed");
            }
        } catch {
            toast.error("Something went wrong while deleting");
        } finally {
            setToDelete(null);
            setDeleteOpen(false);
        }
    };

    const cancelDelete = () => {
        setDeleteOpen(false);
        setToDelete(null);
    };

    const handlePageChange = (nextPage: number) => {
        if (nextPage >= 1 && nextPage <= totalPages) {
            setPage(nextPage);
        }
    };


    return (
        <div className="gallery-container" style={{ padding: "10px 0px" }}>
            <h2 className="gallery-title" style={{ fontFamily: "Karbon-medium" }}>
                Upload Gallery
            </h2>

            <form onSubmit={handleSubmit} className="gallery-form">
                <input
                    type="file"
                    accept="image/*,video/*"
                    ref={fileRef}
                    onChange={handleGalleryChange}
                    style={{ display: "none" }}
                />
                <div
                    onClick={() => fileRef.current?.click()}
                    style={{
                        border: "2px dashed #ccc",
                        padding: "20px",
                        borderRadius: "10px",
                        textAlign: "center",
                        cursor: "pointer",
                        marginTop: 10,
                        background: "#f9f9f9",
                        fontFamily: "Karbon-medium",
                        width: "30%",
                    }}
                >
                    {galleryPreview ? (
                        galleryFile && galleryFile.type.startsWith("video/") ? (
                            <video
                                src={galleryPreview}
                                controls
                                style={{ width: "100%", borderRadius: 10, maxHeight: 300 }}
                            />
                        ) : (
                            <img
                                src={galleryPreview}
                                alt="gallery Preview"
                                style={{ width: "100%", borderRadius: 10, maxHeight: 300 }}
                            />
                        )
                    ) : (
                        "Click here to select image/video"
                    )}

                </div>

                <span
                    style={{
                        fontSize: "12px",
                        color: "#666",
                        fontFamily: "karbon-semi",
                        display: "block",
                        marginTop: 8,
                    }}
                >
                    Accepted formats: JPG, JPEG, PNG, MP4
                </span>

                <button
                    disabled={!galleryPreview}
                    type="submit"
                    className="gallery-button"
                    style={{
                        fontFamily: "Karbon-medium",
                        background: !galleryPreview ? "grey" : "#D9490C",
                        color: "white",
                        border: "none",
                        borderRadius: "20px",
                        padding: "10px",
                        fontSize: "16px",
                        marginTop: 20,
                        width: "30%",
                        cursor: !galleryPreview ? "not-allowed" : "pointer",
                    }}
                >
                    Upload Gallery
                </button>
            </form>

            <div className="table-wrapper">
                <h3 className="table-title">Gallery Items</h3>
                <table className="gallery-table">
                    <thead style={{ fontFamily: "Karbon-medium" }}>
                        <tr>
                            <th>Sr.No.</th>
                            <th>Gallery</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={4}>Loading...</td>
                            </tr>
                        ) : items?.length > 0 ? (
                            items.map((item, index) => (
                                <tr key={item.id || index}>
                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                    <td>
                                        {(() => {
                                            const url = item.gallery || "";
                                            const lower = url.toLowerCase();

                                            return item.isVideo ? (
                                                <video
                                                    src={url}
                                                    className="table-gallery"
                                                    style={{ maxHeight: "60px" }}
                                                    controls
                                                />
                                            ) : (
                                                <img
                                                    src={url}
                                                    className="table-gallery"
                                                    style={{ maxHeight: "60px" }}
                                                />
                                            );
                                        })()}
                                    </td>
                                    <td>
                                        <span
                                            onClick={() => {
                                                openEdit(item);
                                            }}
                                        >
                                            <i
                                                className="bi bi-pencil"
                                                style={{ fontSize: "20px", cursor: "pointer" }}
                                            ></i>
                                        </span>
                                        <span onClick={() => openDelete(item)}>
                                            <i
                                                className="bi bi-trash3"
                                                style={{
                                                    fontSize: "20px",
                                                    cursor: "pointer",
                                                    marginLeft: "10px",
                                                }}
                                            ></i>
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4}>No items found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pagination-footer mb-10">
                <div className="pagination-info" style={{
                    marginTop: "16px",
                    color: "#555",
                    fontWeight: "100",
                    fontSize: "14px",
                    fontFamily: "Karbon"
                }}>
                    Showing {totalItems === 0 ? 0 : startIndex + 1} - {endIndex} of {totalItems}
                </div>

                <div className="pagination-controls">
                    <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="pagination-btn"
                    >
                        Prev
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let firstPage = Math.max(1, Math.min(
                            page - 2,
                            totalPages - 4
                        ));
                        const pageNum = firstPage + i;
                        return (
                            <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`pagination-btn ${pageNum === page ? 'active' : ''}`}
                                style={{
                                    display: pageNum > totalPages ? 'none' : 'inline-block',
                                    margin: '0 2px',
                                    minWidth: '32px',
                                    textAlign: 'center'
                                }}
                            >
                                {pageNum}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="pagination-btn"
                    >
                        Next
                    </button>
                </div>
            </div>

            {editing && (
                <EditGallery
                    isOpen={editOpen}
                    onClose={() => setEditOpen(false)}
                    initialGallery={editing}
                    onSubmit={(data) => applyEdit(data)}
                />
            )}

            <DeleteConfirmationModal
                isOpen={deleteOpen}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Delete Gallery Item"
                itemName={toDelete?.title}
                message={`Are you sure you want to delete the gallery item "${toDelete?.title}"?`}
            />
        </div>
    );
};

export default Gallery;